import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertOctagon, GripVertical, ChevronDown, ChevronUp, Loader2, Route } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AKCENT, OK, CHYBA, POZOR, hm, tint } from './ui';

/**
 * Celá trasa za jízdy (2026-10-24) — nad kartou aktuální zastávky.
 *
 * Sbalená je to jedna lišta „Trasa · 2 z 5". Rozbalená ukáže všechny
 * zastávky ve stylu karty jízdy: odjeté šedé (fajfka / nezastižen), aktuální
 * zvýrazněná, budoucí s úchytem — přetažením se mění pořadí. Přehazovat jde
 * jen to, co ještě čeká; server odjeté drží vpředu a stejné pořadí pak
 * ukazuje PVD i fronta pokladny.
 */
export default function TrasaJizdy({ davkaId, zastavky, optimisticUpdate, onChyba }) {
    const [otevreno, setOtevreno] = useState(false);
    const [uklada, setUklada] = useState(false);
    const casovac = useRef(null);

    const ceka = (z) => (z.stav_doruceni ?? 'ceka') === 'ceka';
    const hotove = zastavky.filter((z) => !ceka(z));
    const [cekajici, setCekajici] = useState(() => zastavky.filter(ceka));

    // Ze serveru jen když se reálně změnilo složení nebo pořadí — jinak by
    // refetch přepsal rozdělané přetahování.
    const podpis = zastavky.filter(ceka).map((z) => z.objednavka_id).join(',');
    useEffect(() => { setCekajici(zastavky.filter(ceka)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [podpis]);

    // Jako v přípravě dávky: úchyt má touch-action none, stačí pointer.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const ulozit = useCallback(async (noveCekajici) => {
        const poradi = [...hotove, ...noveCekajici].map((z) => z.objednavka_id);
        setUklada(true);
        try {
            const r = await fetch(`/api/rozvoz/davky/${davkaId}/preradit`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ poradi }),
            });
            if (!r.ok) throw new Error(`Pořadí se neuložilo (HTTP ${r.status})`);
            const json = await r.json();
            const nove = json.davka?.zastavky;
            if (nove?.length) optimisticUpdate?.((prev) => (prev ? { ...prev, zastavky: nove } : prev));
        } catch (e) {
            onChyba?.(e?.message || String(e));
            setCekajici(zastavky.filter(ceka));
        } finally {
            setUklada(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [davkaId, hotove.map((z) => z.objednavka_id).join(',')]);

    const poPretazeni = useCallback(({ active, over }) => {
        if (!over || active.id === over.id) return;
        const a = cekajici.findIndex((z) => z.objednavka_id === active.id);
        const b = cekajici.findIndex((z) => z.objednavka_id === over.id);
        if (a === -1 || b === -1) return;
        const nove = arrayMove(cekajici, a, b);
        setCekajici(nove);
        // Hned i do karty jízdy — aktuální zastávka je první čekající.
        optimisticUpdate?.((prev) => (prev ? { ...prev, zastavky: [...hotove, ...nove] } : prev));
        clearTimeout(casovac.current);
        casovac.current = setTimeout(() => ulozit(nove), 250);
    }, [cekajici, hotove, optimisticUpdate, ulozit]);

    if (zastavky.length < 2) return null;

    const celkem = zastavky.length;
    const Sipka = otevreno ? ChevronDown : ChevronUp;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button type="button" onClick={() => setOtevreno(!otevreno)} aria-expanded={otevreno} style={{
                display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', borderRadius: 99,
                border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)',
                color: 'var(--text-primary)', fontSize: 14, fontWeight: 800, cursor: 'pointer',
            }}>
                <Route size={16} style={{ color: AKCENT }} />
                <span style={{ flex: 1, textAlign: 'left' }}>
                    Trasa · {Math.min(hotove.length + 1, celkem)} z {celkem}
                </span>
                {uklada && <Loader2 size={15} className="animate-spin" style={{ color: 'var(--text-secondary)' }} />}
                <Sipka size={18} />
            </button>

            {otevreno && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '42vh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
                    {hotove.map((z, i) => <KartaTrasy key={z.objednavka_id} z={z} poradi={i + 1} stav="hotova" />)}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={poPretazeni}>
                        <SortableContext items={cekajici.map((z) => z.objednavka_id)} strategy={verticalListSortingStrategy}>
                            {cekajici.map((z, i) => (
                                <PretahovaciKarta key={z.objednavka_id} z={z} poradi={hotove.length + i + 1} aktualni={i === 0} />
                            ))}
                        </SortableContext>
                    </DndContext>
                    {cekajici.length > 1 && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', padding: '2px 0 4px' }}>
                            Pořadí změníš přetažením za úchyt. Uvidí ho i PVD a pokladna.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function PretahovaciKarta({ z, poradi, aktualni }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: z.objednavka_id });
    return (
        <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 1 }}>
            <KartaTrasy z={z} poradi={poradi} stav={aktualni ? 'aktualni' : 'ceka'} tazena={isDragging}
                uchyt={(
                    <button {...attributes} {...listeners} aria-label="Přetáhnout pořadí" style={{
                        width: 34, alignSelf: 'stretch', border: 'none', background: 'transparent', color: 'var(--text-secondary)',
                        cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none', flexShrink: 0,
                    }}>
                        <GripVertical size={18} />
                    </button>
                )}
            />
        </div>
    );
}

/** Karta zastávky ve stylu karty jízdy — velké číslo, zákazník, adresa, čas. */
function KartaTrasy({ z, poradi, stav, tazena = false, uchyt = null }) {
    const hotova = stav === 'hotova';
    const nezastizen = z.stav_doruceni === 'nezastizen';
    const aktualni = stav === 'aktualni';
    const pozde = (z.rozdil_sec ?? 0) > 0;
    const cas = hotova ? hm(z.cas_doruceni_skutecny) : hm(z.cas_predpoklad);

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 62, padding: uchyt ? '8px 12px 8px 2px' : '8px 12px',
            borderRadius: 18,
            background: aktualni ? tint(AKCENT, 0.12) : 'rgba(255,255,255,0.05)',
            border: `1px solid ${tazena ? AKCENT : aktualni ? tint(AKCENT, 0.55) : 'rgba(255,255,255,0.10)'}`,
            opacity: hotova ? 0.45 : 1,
            boxShadow: tazena ? '0 10px 24px rgba(0,0,0,0.45)' : 'none',
        }}>
            {uchyt}
            <div style={{ fontFamily: 'var(--pismo-display)', fontSize: 26, lineHeight: 1, flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 48 }}>
                #{z.cislo_denni ?? z.cislo_objednavky}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {z.jmeno || 'Zákazník'}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {poradi}. · {z.adresa || '—'}
                </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {hotova ? (
                    nezastizen
                        ? <AlertOctagon size={20} style={{ color: CHYBA }} aria-label="Nezastižen" />
                        : <CheckCircle2 size={20} style={{ color: OK }} aria-label="Doručeno" />
                ) : (
                    <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: pozde ? POZOR : OK }}>{cas}</div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {hotova ? (cas || '') : aktualni ? 'teď' : 'příjezd'}
                </div>
            </div>
        </div>
    );
}
